import { useState, useRef, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import { fetchDominioData } from "../../../../../fetchDominioData";
import DatePickerField from "../../../../../components/DatePickerField";
import dayjs from "dayjs";

import {
  Box,
  Drawer,
  Stack,
  Divider,
  Typography,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CloudDoneIcon from "@mui/icons-material/CloudDone";

import useHrmEmployeeStore from "../../../../../stores/hrEmployee";
import useAuthStore from "../../../../../stores/auth";
import {
  formatExperienceDraftDate,
  getExperienceDaysTotal,
  hasExperienceProrrogation,
  isDeterminedEmploymentVinculo,
  isExperienceDaysTotalValid,
  getDeterminedContractMinEndDate,
  mergeExperienceIntoDraft,
  maxExperienceDaysTotal,
  parsePositiveDays,
  sanitizeDeterminedContractEnd,
  syncDeterminedContractStartFromAdmission,
} from "../../../../../utils/experienceContractDates";

const tipoVinculo = [
  { label: "Nao aparece no sefip", value: 0 },
  { label: "Celetista", value: 1 },
  { label: "Diretor sem vinculo, SEM FGTS", value: 11 },
  { label: "Autonomo", value: 13 },
  { label: "Cooperado", value: 17 },
  { label: "Estatutario", value: 2 },
  { label: "Trabalhador Avulso", value: 3 },
  { label: "Trabalhador Temporario Lei 6.019/74", value: 4 },
  { label: "Celetista Contrato Prazo Determinado", value: 5 },
  { label: "Estagiario", value: 50 },
  { label: "Menor Aprendiz", value: 53 },
  { label: "Intermitente", value: 111 },
];

const categoriaOptions = [
  { label: "Mensalista", value: 1 },
  { label: "Semanalista", value: 3 },
  { label: "Diarista", value: 4 },
  { label: "Horista", value: 5 },
  { label: "Tarefeiro", value: 6 },
  { label: "Comissionado", value: 7 },
  { label: "Diretor", value: 8 },
];

const tipoIndicativoAdmissao = [
  { label: "1º emprego", value: 1 },
  { label: "Normal", value: 2 },
  { label: "Decorrente de ação fiscal", value: 3 },
  { label: "Decorrente de decisão judicial", value: 4 },
];

const tipoAdmissaoOptions = [
  { label: "Admissão", value: 1 },
  { label: "Admissão por sucessão, incorporação ou fusão", value: 2 },
  { label: "Transferência com ônus", value: 3 },
  { label: "Transferência sem ônus", value: 4 },
  { label: "Reintegração", value: 5 },
];

const multiplosVinculosOptions = [
  { label: "Não", value: "N" },
  { label: "Sim, abaixo do teto", value: "B" },
  { label: "Sim, neste banco", value: "D" },
  { label: "Sim, acima do teto", value: "T" },
];

const pagouSindicalOptions = [
  { label: "Não", value: "N" },
  { label: "Sim", value: "S" },
  { label: "Profissional liberal", value: "L" },
  { label: "Não pagar", value: "X" },
];

const semIntervaloValue = "Não possui";

const HRManagementEmployeeAdmission = () => {
  const [state, setState] = useState(false);
  const [cargos, setCargos] = useState([]);
  const [cargosLoading, setCargosLoading] = useState(false);
  const [cargoNaoEncontrado, setCargoNaoEncontrado] = useState(false);
  const openRef = useRef(null);
  const saveDraft = useHrmEmployeeStore((state: any) => state.saveDraft);
  const changeDraft = useHrmEmployeeStore((state: any) => state.changeDraft);
  const setData = useHrmEmployeeStore((state: any) => state.setState);
  const isSaving = useHrmEmployeeStore((state: any) => state.isSaving);
  const data = useHrmEmployeeStore((state: any) => state.data);
  const company = useAuthStore((state) => state.company);
  const addDrawersRef = useHrmEmployeeStore(
    (state: any) => state.addDrawersRef,
  );

  const minAdmissionDate = (() => {
    let targetDate = dayjs();
    let businessDays = 0;
    while (businessDays < 3) {
      targetDate = targetDate.add(1, "day");
      if (targetDate.day() !== 0 && targetDate.day() !== 6) {
        businessDays++;
      }
    }
    return targetDate;
  })();

  const hasExperience =
    String(data.draft?.contrato_experiencia) === "1" ||
    data.draft?.contrato_experiencia === "Possui";
  const hasDeterminedVinculo = isDeterminedEmploymentVinculo(
    data.draft?.tipo_vinculo,
  );
  const showDeterminedContractFields = hasDeterminedVinculo;
  const determinedContractMinEndDate = getDeterminedContractMinEndDate(
    data.draft ?? {},
  );
  const experienceDaysTotal = getExperienceDaysTotal(
    data.draft?.dias_experiencia,
    data.draft?.dias_prorrogacao,
  );
  const hasProrrogation = hasExperienceProrrogation(data.draft ?? {});
  const semIntervalo = data.draft?.horario_intervalo === semIntervaloValue;
  const experienceDaysInvalid =
    hasExperience &&
    parsePositiveDays(data.draft?.dias_experiencia) !== null &&
    !isExperienceDaysTotalValid(
      data.draft?.dias_experiencia,
      data.draft?.dias_prorrogacao,
    );

  const applyExperienceDraft = (draft: Record<string, unknown>) => {
    setData({ ...data, draft: mergeExperienceIntoDraft(draft) });
  };

  const handleExperienceContractToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    const normalized =
      value === "1" || value === "Possui"
        ? "1"
        : value === "0" || value === "Não Possui"
        ? "0"
        : value;

    const nextDraft: Record<string, unknown> = {
      ...data.draft,
      contrato_experiencia: normalized,
    };

    if (normalized === "1") {
      if (!nextDraft.dias_experiencia) {
        nextDraft.dias_experiencia = 30;
      }
    } else if (normalized === "0") {
      nextDraft.dias_experiencia = "";
      nextDraft.dias_prorrogacao = "";
      nextDraft.inicio_experiencia = "";
      nextDraft.fim_experiencia = "";
      nextDraft.fim_prorrogacao_experiencia = "";
      nextDraft.motivo_prorrogacao = "";
    }

    const draftToSave =
      normalized === "1" ? mergeExperienceIntoDraft(nextDraft) : nextDraft;

    setData({ ...data, draft: draftToSave });
    saveDraft();
  };

  const handleExperienceDaysChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    applyExperienceDraft({
      ...data.draft,
      [event.target.name]: event.target.value,
    });
  };

  const handleTipoVinculoChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let nextDraft: Record<string, unknown> = {
      ...data.draft,
      tipo_vinculo: event.target.value,
    };

    if (isDeterminedEmploymentVinculo(event.target.value)) {
      nextDraft = sanitizeDeterminedContractEnd(
        syncDeterminedContractStartFromAdmission(nextDraft),
      );
    } else {
      nextDraft.inicio_contrato_determinado = "";
      nextDraft.fim_contrato_determinado = "";
    }

    setData({ ...data, draft: nextDraft });
    saveDraft();
  };

  const handleAdmissionDateChange = (name: string, value: string) => {
    let nextDraft = { ...data.draft, [name]: value };
    if (hasExperience) {
      nextDraft = mergeExperienceIntoDraft(nextDraft);
    }
    if (isDeterminedEmploymentVinculo(nextDraft.tipo_vinculo)) {
      nextDraft = sanitizeDeterminedContractEnd(
        syncDeterminedContractStartFromAdmission(nextDraft),
      );
    }
    setData({ ...data, draft: nextDraft });
    saveDraft();
  };

  const handleDeterminedContractEndChange = (name: string, value: string) => {
    const nextDraft = sanitizeDeterminedContractEnd({
      ...data.draft,
      [name]: value,
    });
    setData({ ...data, draft: nextDraft });
    saveDraft();
  };

  const handleSemIntervaloChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;
    setData({
      ...data,
      draft: {
        ...data.draft,
        horario_intervalo: checked ? semIntervaloValue : "",
      },
    });
    saveDraft();
  };

  useEffect(() => addDrawersRef({ name: "admission", ref: openRef }), []);

  useEffect(() => {
    if (data.draft?.cargo_customizado) {
      setCargoNaoEncontrado(true);
    }
  }, [data.draft?.cargo_customizado]);

  useEffect(() => {
    if (!state) return;
    (async () => {
      setCargosLoading(true);
      try {
        const response = await fetchDominioData(
          `/empresa/${company.reference}/cargos`,
          { method: "GET" },
        );
        if (response) setCargos(response.rows);
      } catch (error) {
        console.error(error);
      }
      setCargosLoading(false);
    })();
  }, [state, company.reference]);

  return (
    <>
      <Button
        fullWidth
        ref={openRef}
        variant="outlined"
        onClick={() => setState(true)}
        disabled={data?.status !== "editing"}
      >
        <Stack spacing={1} alignItems="center">
          <VerifiedUserIcon fontSize="large" />
          <div>Admissão</div>
        </Stack>
      </Button>
      <Drawer anchor="left" open={state} onClose={() => setState(false)}>
        <Box sx={{ p: 3, pt: 2, width: 400 }} height="100%">
          <Stack height="100%" spacing={2}>
            <Stack direction="row" alignItems="center">
              <Typography variant="body1" sx={{ flex: 1 }}>
                Admissão
              </Typography>
              {data.status === "editing" && (
                <Chip
                  color="success"
                  variant="outlined"
                  icon={
                    isSaving ? (
                      <CircularProgress size={18} />
                    ) : (
                      <CloudDoneIcon fontSize="small" />
                    )
                  }
                  label={isSaving ? "Salvando..." : "Tudo Salvo"}
                />
              )}
              <IconButton onClick={() => setState(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider />
            <TextField
              name="cargo"
              fullWidth
              variant="filled"
              select
              label="Cargo"
              value={data.draft?.cargo ?? ""}
              SelectProps={{ native: true }}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required={!cargoNaoEncontrado}
              disabled={cargoNaoEncontrado || cargosLoading}
            >
              <option value=""></option>
              {cargos.map((cargo: any) => (
                <option value={cargo.i_cargos} key={cargo.i_cargos}>
                  {cargo.nome}
                </option>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={cargoNaoEncontrado}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setCargoNaoEncontrado(checked);
                    if (checked) {
                      changeDraft({ target: { name: "cargo", value: "" } });
                    } else {
                      changeDraft({
                        target: { name: "cargo_customizado", value: "" },
                      });
                    }
                    saveDraft();
                  }}
                />
              }
              label="Outro cargo (informe abaixo)"
            />
            {cargoNaoEncontrado && (
              <TextField
                name="cargo_customizado"
                fullWidth
                variant="filled"
                label="Digite o cargo desejado"
                value={data.draft?.cargo_customizado ?? ""}
                onChange={changeDraft}
                onBlur={() => saveDraft()}
                required
              />
            )}
            <TextField
              name="centro_de_custo"
              fullWidth
              variant="filled"
              label="Departamento"
              value={data.draft?.centro_de_custo ?? ""}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
            />
            <NumericFormat
              name="valor_salario"
              customInput={TextField}
              fullWidth
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              allowNegative={false}
              prefix="R$ "
              variant="filled"
              label="Salário"
              value={data.draft?.valor_salario ?? ""}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required
            />
            <DatePickerField
              name="data_admissao"
              label="Data de admissão"
              required
              requiredtxt="Data de admissão é obrigatória"
              variant="filled"
              register={() => ({})}
              get={(name: string) => data.draft?.[name] || null}
              set={handleAdmissionDateChange}
              minDate={minAdmissionDate}
            />
            <TextField
              name="tipo_vinculo"
              fullWidth
              variant="filled"
              select
              label="Vínculo empregatício"
              value={data.draft?.tipo_vinculo ?? ""}
              SelectProps={{ native: true }}
              onChange={handleTipoVinculoChange}
              onBlur={() => saveDraft()}
              required
            >
              <option value=""></option>
              {tipoVinculo.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </TextField>
            {showDeterminedContractFields && (
              <>
                <TextField
                  fullWidth
                  variant="filled"
                  label="Início do contrato determinado"
                  value={formatExperienceDraftDate(
                    data.draft?.inicio_contrato_determinado,
                  )}
                  disabled
                />
                <DatePickerField
                  name="fim_contrato_determinado"
                  label="Fim do contrato determinado"
                  required
                  requiredtxt="Informe o fim do contrato determinado"
                  variant="filled"
                  register={() => ({})}
                  get={(name: string) => data.draft?.[name] || null}
                  set={handleDeterminedContractEndChange}
                  minDate={determinedContractMinEndDate}
                />
              </>
            )}
            <TextField
              name="multiplos_vinculos"
              fullWidth
              variant="filled"
              select
              label="Múltiplos Vínculos?"
              value={data.draft?.multiplos_vinculos ?? ""}
              SelectProps={{ native: true }}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required
            >
              <option value=""></option>
              {multiplosVinculosOptions.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </TextField>
            <TextField
              name="pagou_contribuicao_sindical"
              fullWidth
              variant="filled"
              select
              label="Pagou contribuição sindical?"
              value={data.draft?.pagou_contribuicao_sindical ?? ""}
              SelectProps={{ native: true }}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required
            >
              <option value=""></option>
              {pagouSindicalOptions.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </TextField>
            <TextField
              name="jornada"
              fullWidth
              variant="filled"
              label="Jornada do empregado"
              value={data.draft?.jornada ?? ""}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required
              placeholder="ex: Segunda à sexta, 12x36, Segunda à sábado, etc."
            />
            <TextField
              name="horario"
              fullWidth
              variant="filled"
              label="Horário"
              value={data.draft?.horario ?? ""}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required
              placeholder="ex: das 08:00 às 17:00"
            />
            <TextField
              name="horario_intervalo"
              fullWidth
              variant="filled"
              label="Horário de intervalo/almoço"
              value={
                semIntervalo
                  ? semIntervaloValue
                  : data.draft?.horario_intervalo ?? ""
              }
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required
              disabled={semIntervalo}
              placeholder="ex: das 12h00 às 13h00, das 21h às 22h..."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={semIntervalo}
                  onChange={handleSemIntervaloChange}
                />
              }
              label="Não possui intervalo"
            />
            <TextField
              name="categoria"
              fullWidth
              variant="filled"
              select
              label="Categoria"
              value={data.draft?.categoria ?? ""}
              SelectProps={{ native: true }}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required
            >
              <option value=""></option>
              {categoriaOptions.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </TextField>
            <TextField
              name="tipo_indicativo_admissao"
              fullWidth
              variant="filled"
              select
              label="Tipo indicativo da admissão"
              value={data.draft?.tipo_indicativo_admissao ?? ""}
              SelectProps={{ native: true }}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required
            >
              <option value=""></option>
              {tipoIndicativoAdmissao.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </TextField>
            <TextField
              name="tipo_admissao"
              fullWidth
              variant="filled"
              select
              label="Tipo de admissão"
              value={data.draft?.tipo_admissao ?? ""}
              SelectProps={{ native: true }}
              onChange={changeDraft}
              onBlur={() => saveDraft()}
              required
            >
              <option value=""></option>
              {tipoAdmissaoOptions.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </TextField>
            <TextField
              name="contrato_experiencia"
              fullWidth
              variant="filled"
              select
              label="Possui Contrato de Experiência?"
              value={
                data.draft?.contrato_experiencia === "Possui"
                  ? "1"
                  : data.draft?.contrato_experiencia ?? ""
              }
              SelectProps={{ native: true }}
              onChange={handleExperienceContractToggle}
              onBlur={() => saveDraft()}
            >
              <option value=""></option>
              <option value="0">Não possui</option>
              <option value="1">Possui</option>
            </TextField>
            {hasExperience && (
              <>
                <TextField
                  name="dias_experiencia"
                  fullWidth
                  variant="filled"
                  label="Quantidade de dias de experiência"
                  type="number"
                  inputProps={{ min: 1, max: maxExperienceDaysTotal }}
                  value={data.draft?.dias_experiencia ?? ""}
                  onChange={handleExperienceDaysChange}
                  onBlur={() => saveDraft()}
                  required
                  error={experienceDaysInvalid}
                  helperText={
                    experienceDaysInvalid
                      ? `A soma não pode passar de ${maxExperienceDaysTotal} dias (atual: ${experienceDaysTotal}).`
                      : undefined
                  }
                />
                <TextField
                  fullWidth
                  variant="filled"
                  label="Início da experiência"
                  value={formatExperienceDraftDate(
                    data.draft?.inicio_experiencia,
                  )}
                  disabled
                />
                <TextField
                  fullWidth
                  variant="filled"
                  label="Fim da experiência"
                  value={formatExperienceDraftDate(data.draft?.fim_experiencia)}
                  disabled
                />
                <TextField
                  name="dias_prorrogacao"
                  fullWidth
                  variant="filled"
                  label="Quantidade de dias da prorrogação da experiência"
                  type="number"
                  inputProps={{ min: 1, max: maxExperienceDaysTotal }}
                  value={data.draft?.dias_prorrogacao ?? ""}
                  onChange={handleExperienceDaysChange}
                  onBlur={() => saveDraft()}
                  error={experienceDaysInvalid}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  label="Fim da prorrogação da experiência"
                  value={formatExperienceDraftDate(
                    data.draft?.fim_prorrogacao_experiencia,
                  )}
                  disabled
                />
                <FormHelperText sx={{ mt: -1, color: "text.secondary" }}>
                  Datas recalculadas ao alterar a admissão ou os dias
                  informados.
                </FormHelperText>
                <TextField
                  name="motivo_prorrogacao"
                  fullWidth
                  variant="filled"
                  label="Motivo da prorrogação do contrato de experiência"
                  value={data.draft?.motivo_prorrogacao ?? ""}
                  onChange={changeDraft}
                  onBlur={() => saveDraft()}
                  required={hasProrrogation}
                />
              </>
            )}
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default HRManagementEmployeeAdmission;
